/**
 * Maps Tailwind `bg-*` tokens from settings `color` strings to HSL triplets (space-separated)
 * for use in `hsl(var(--gantt-task-status) / …)` and inline `hsl(…)`.
 */
const TAILWIND_BG_TO_HSL_TRIPLET: Record<string, string> = {
    'bg-slate-100': '215 16% 46%',
    'bg-gray-100': '215 16% 46%',
    'bg-zinc-100': '240 5% 42%',
    'bg-neutral-100': '0 0% 45%',
    'bg-stone-100': '25 6% 44%',
    'bg-red-100': '0 84% 55%',
    'bg-orange-100': '24 95% 48%',
    'bg-amber-100': '38 92% 45%',
    'bg-yellow-100': '45 93% 42%',
    'bg-lime-100': '85 85% 38%',
    'bg-green-100': '142 76% 34%',
    'bg-emerald-100': '160 84% 35%',
    'bg-teal-100': '173 80% 36%',
    'bg-cyan-100': '189 94% 38%',
    'bg-sky-100': '199 89% 48%',
    'bg-blue-100': '221 83% 53%',
    'bg-indigo-100': '239 84% 58%',
    'bg-violet-100': '258 90% 58%',
    'bg-purple-100': '271 81% 56%',
    'bg-fuchsia-100': '293 69% 52%',
    'bg-pink-100': '330 81% 55%',
    'bg-rose-100': '347 77% 52%',
};

export function extractBackgroundToken(colorField: string): string {
    const token = colorField
        .trim()
        .split(/\s+/)
        .find((c) => c.startsWith('bg-'));
    return token ?? 'bg-gray-100';
}

export function projectTaskColorToHslTriplet(colorField: string): string {
    const token = extractBackgroundToken(colorField);
    return TAILWIND_BG_TO_HSL_TRIPLET[token] ?? '215 20% 65%';
}

export function hslTripletToCss(triplet: string): string {
    return `hsl(${triplet})`;
}

export function resolveTaskStatusSwatchFromSettings(
    task: { status?: string; type?: 'project' | 'task' },
    statuses: Array<{ value: string; color: string }>,
): string {
    if (task.type === 'project') {
        return hslTripletToCss(projectTaskColorToHslTriplet('bg-blue-100'));
    }
    const meta = statuses.find((s) => s.value === task.status);
    return hslTripletToCss(projectTaskColorToHslTriplet(meta?.color ?? 'bg-gray-100'));
}

export function ganttStatusClassSlug(value: string): string {
    const safe = value.replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'status';
    return `gantt-status-${safe}`;
}

export function buildGanttTaskStatusCssRules(
    wrapperSelectorList: string[],
    statuses: Array<{ value: string; color: string }>,
    statusCssProperty: '--gantt-task-status' | '--portfolio-gantt-task-status' = '--gantt-task-status',
): string {
    if (wrapperSelectorList.length === 0 || statuses.length === 0) {
        return '';
    }
    return statuses
        .map((s) => {
            const slug = ganttStatusClassSlug(s.value);
            const hsl = projectTaskColorToHslTriplet(s.color);
            return wrapperSelectorList.map((w) => `${w} .gantt_task_line.${slug}{${statusCssProperty}:${hsl};}`).join('');
        })
        .join('\n');
}

export function buildPortfolioStaticBarStatusCssRules(rootSelector: string, statuses: Array<{ value: string; color: string }>): string {
    if (!statuses.length) {
        return '';
    }
    return statuses
        .map((s) => {
            const slug = ganttStatusClassSlug(s.value);
            const hsl = projectTaskColorToHslTriplet(s.color);
            return `${rootSelector} .portfolio-gantt-static-bar.${slug}{--portfolio-gantt-task-status:${hsl};}`;
        })
        .join('\n');
}
